import {  Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs';
import { HttpErrorHandlerService } from '../../services/http-error-handler.service';
import { NotificationsService } from '../../services/notifications.service';
import { UserService } from '../../services/user.service';
import { AutoNotifyBaseComponent } from '../../shared-new/components/auto-notify-base/auto-notify-base.component';
import { SaleMemberStatus } from '../../shared-new/enums/sale-member-status.enum';
import { ToastService } from '../../shared-new/services/toast.service';
import { EventsEmitter } from '../../shared/event-emitter/EventEmitter';
import { AuthService } from '../../services/auth.service';
import swal from 'sweetalert2';
import { NotificationTemplateComponent } from '../../shared/notification-template/notification-template.component';

@Component({
  selector: 'app-review-vet-profile-popup',
  templateUrl: './review-vet-profile-popup.component.html',
  styleUrls: ['./review-vet-profile-popup.component.scss'],
})
export class ReviewVetProfilePopupComponent
  extends AutoNotifyBaseComponent
  implements OnInit {


  public isLoader = false;

  public responseSuccess: any = null;
  public responseError: any = null;
  public isApproved: boolean = null;
  public comments: string;
  public isSubmitAccess: boolean = false;
  public user: any;
  public isViewAccess: boolean = false;
  public memberviewAccess: boolean = true;
  public membersubmitAccess: boolean = true;
  public memberAllSaleAccess: boolean = true;
  public submitted = false;

  @Input() public data: any;
  showMessageBox = false;

  deniedClicked = false;
  approvedClicked = false;
  salesAdminInfo = '';
  /** True while the Denied (type 70) envelope is being fetched after the deny reason was entered. */
  isDenyLoading = false;
  public static readonly REQUEST_UNAVAILABLE_MESSAGE = 'This request was already handled or no longer exists.';

  /**
   * A decision needs the real pending SaleMemberRequest. Without its id the API refuses (and before the
   * security release it emailed the vet "approved" while granting nothing), so the popup is read-only.
   */
  public get isRequestUnavailable(): boolean {
    return !!this.data?.requestUnavailable || !(this.data?.saleMemberRequestId > 0);
  }

  public get requestUnavailableMessage(): string {
    return ReviewVetProfilePopupComponent.REQUEST_UNAVAILABLE_MESSAGE;
  }
  @ViewChild(NotificationTemplateComponent, { static: false })
  notificationTemplate!: NotificationTemplateComponent;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly httpErrorHandleService: HttpErrorHandlerService,
    public readonly modal: NgbActiveModal,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly notificationService: NotificationsService,
    protected override readonly eventEmitter: EventsEmitter
  ) {
    super(eventEmitter);
  }

  ngOnInit(): void {
    this.viewMode = "Submit";
    this.isLoader = true;
    this.loadProfile();
    this.loadUser();

    if (!this.isRequestUnavailable) {
      this.approvedClick();
    }
    setTimeout(() => {
      const el = document.querySelector('.modal');
      if (el) el.scrollTop = 0;
    }, 100);

  }
  onSubmit(): void {
    this.changeViewModeToSubmit();
  }
  onCancel(): void {
    this.changeViewModeToSubmit();
  }

  onDeniedChange(event: MatCheckboxChange): void {
    this.isApproved = !event.checked;
  }
  onApprovedChange(event: MatCheckboxChange): void {
    this.isApproved = event.checked;
  }
  onSubmitAccessChange() {
    if (this.membersubmitAccess) {
      this.memberviewAccess = true;
    }
  }
  openMessageBox(): void {
    this.showMessageBox = true;
  }

  public loadUser(): void {
    this.authService.getUserData().subscribe((data) => {
      if (data) {
        this.user = JSON.parse(data);
      }
    });
  }
  deniedClick() {
    if (this.isRequestUnavailable || this.isDenyLoading) {
      return;
    }
    swal.fire({
      title: 'Deny Reason',
      inputPlaceholder: 'Enter reason',
      input: 'textarea',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Deny',
      confirmButtonColor: '#f65858',
    }).then((result) => {
      const reason = typeof result.value === 'string' ? result.value.trim() : '';
      if (reason) {
        this.denyWithDeniedEnvelope(reason);
      }
    });
  }

  /**
   * Deny sends the ACCESS DENIED message (type 70), never the approval one. The Denied envelope is fetched
   * for this vet and sale (NotifyVetInfo status 4) and the reason is added to the email and SMS text.
   * The reviewer's Auto Notify choice and recipient selection (made on the approval preview) carry over.
   */
  private denyWithDeniedEnvelope(reason: string): void {
    const notifyWanted = this.allChecked;
    // If the approval preview loaded, honour who the reviewer left ticked there; otherwise everyone resolved.
    const previewLoaded = (this.recipients?.length ?? 0) > 0;
    const selectedKeys = new Set((this.getSelectedRecipients() || []).map((r: any) => ReviewVetProfilePopupComponent.recipientKey(r)));
    this.isDenyLoading = true;
    this.notificationService.getNotifyVetInfo(this.data.memberId, SaleMemberStatus.Denied, this.data.saleId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.isDenyLoading = false;
          const deniedTemplate = ReviewVetProfilePopupComponent.withDenyReason(response?.messageTemplate, reason);
          const hasTemplate = !!deniedTemplate && deniedTemplate.notificationTypeId != 0;
          const recipients = notifyWanted && hasTemplate
            ? (response?.recipients || [])
              .filter((r: any) => !previewLoaded || selectedKeys.has(ReviewVetProfilePopupComponent.recipientKey(r)))
              .map((r: any) => ({ ...r, isSelected: true }))
            : [];
          if (notifyWanted && !hasTemplate) {
            this.toastService.warning('No "Access Denied" message is set up for this sales company, so the vet will not be notified.', 'Not notified');
          }
          this.data.notificationData = { ...(response || {}), messageTemplate: deniedTemplate, recipients };
          this.deniedClicked = true;
          this.approvedClicked = false;
          this.comments = reason;
          this.submitReview();
        },
        error: (error) => {
          this.isDenyLoading = false;
          // Never fall back to the approval envelope: without the Denied one nothing is submitted.
          this.httpErrorHandleService.ShowCustomErrorMessage(error, 'Could not load the Access Denied message');
        }
      });
  }

  /** Same person on the approval and denial previews (both are resolved for the requesting vet clinic). */
  private static recipientKey(r: any): string {
    return `${r?.userId ?? ''}|${(r?.email ?? '').toLowerCase()}|${r?.phone ?? ''}|${r?.consignorContactId ?? ''}`;
  }

  /** Adds the deny reason to the Denied message (email body is plain text with newlines; SMS one line). */
  public static withDenyReason(template: any, reason: string): any {
    if (!template) {
      return template;
    }
    const line = `Reason: ${reason}`;
    const email: string = template.emailMessage ?? '';
    const sms: string = template.smsMessage ?? '';
    return {
      ...template,
      emailMessage: email ? `${email.trimEnd()}\n\n${line}` : line,
      smsMessage: sms ? `${sms.trimEnd()} ${line}` : line,
    };
  }

  approvedClick(): void {
    this.notificationService.getNotifyVetInfo(this.data.memberId, SaleMemberStatus.Approved, this.data.saleId).pipe(takeUntil(this.unsubscribe$)).subscribe((response) => {
      this.data.notificationData = response;
      this.messageTemplate = { ...this.data.notificationData.messageTemplate };
      this.recipients = [...this.data.notificationData.recipients];
      this.setNotificationTemplateRef(this.notificationTemplate);
    });
    this.deniedClicked = false;
    this.approvedClicked = true;
    this.comments = `Approved by ${this.user?.firstName ?? ''} ${this.user?.lastName ?? ''} at ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
  }
  closePopup(): void {
    this.modal.close(JSON.stringify(this.data));
  }
  setViewAccess(checked: boolean): void {
    this.isSubmitAccess = checked;
    if (checked) {
      this.isViewAccess = true;
    }
  }
  submitReview(): void {
    if (this.isRequestUnavailable) {
      this.toastService.error(this.requestUnavailableMessage, 'Error!');
      return;
    }
    if (!this.data.notificationData) {
      this.toastService.error('The notification is still loading, please try again.', 'Error!');
      return;
    }
    if (!this.approvedClicked && !this.deniedClicked) {
      this.toastService.error('Please select a status!', 'Error!');
      return;
    }

    if (this.deniedClicked && !this.comments) {
      this.toastService.error('Please provide a reason for denying the request!', 'Error!');
      return;
    }
    if (this.approvedClicked && !this.memberviewAccess) {
      this.toastService.error('Please select access tickbox!', 'Error!');
      return;
    }
    if (this.approvedClicked) {
      this.data.isApproved = true;
      this.data.comments = '';
      this.data.isSubmitAccess = this.membersubmitAccess;
      this.data.isViewAccess = this.memberviewAccess;
      this.data.isAllSaleAccess = this.memberAllSaleAccess;
    } else {
      this.data.isApproved = false;
      this.data.comments = this.comments;
      this.data.isSubmitAccess = false;
      this.data.isViewAccess = false;
    }
    if (this.deniedClicked) {
      // notificationData is the Denied envelope built in denyWithDeniedEnvelope (never the approval one).
    } else if (this.allChecked) {
      this.data.notificationData.recipients = [...(this.getSelectedRecipients() || [])]
      this.data.notificationData.messageTemplate = this.messageTemplate;
    } else {
      this.data.notificationData.recipients = []
    }
    this.modal.close(JSON.stringify(this.data));
  }
  public loadProfile(): void {
    this.userService.getMemberAccess(this.data?.memberId, this.data.saleId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          this.isSubmitAccess = response.isSubmitAccess;
          this.isViewAccess = response.isViewOnly;
          this.membersubmitAccess = this.isSubmitAccess;
          this.memberAllSaleAccess = response.isAllSaleAccess;
          this.memberviewAccess = this.membersubmitAccess ? true : this.isViewAccess;
          this.isLoader = false;
        },
        (error) => {
          this.isLoader = false;
          if (error) {
            this.httpErrorHandleService.ShowErrorMessage(error);
          }
        }
      );

  }
}
